import { apiErrorMessage } from "@/utils/apiError-helper";

/**
 * A funding failure, in words the person reading it can act on.
 *
 * The API's own messages are written for the boundary they protect — "Forbidden: target out of
 * scope", "Invalid id", "in_flight" — and every one of them is correct and useless to a client
 * looking at a declined card. They also leak the shape of the guard that produced them, which is a
 * second reason not to print them verbatim on a client's screen.
 *
 * Anything unrecognised falls through to the server's text rather than to a shrug: a message nobody
 * mapped is still better than "Something went wrong", and it is what makes the next unknown case
 * debuggable instead of invisible.
 */
const FRIENDLY: { match: RegExp; message: string }[] = [
  {
    match: /necessary permissions|forbidden/i,
    message: "That card belongs to a different account, so it cannot fund this campaign.",
  },
  {
    match: /invalid id/i,
    message: "We could not identify this campaign. Please reload the page and try again.",
  },
  {
    match: /in.?flight|already pending/i,
    message: "A charge for this campaign is already in progress. Give it a moment and check back.",
  },
  {
    match: /not funded|no terms|not allocated/i,
    message: "This campaign does not top itself up, so there is nothing to charge.",
  },
  {
    match: /no payment method|payment method required/i,
    message: "There is no card saved for this campaign yet. Add one to start funding it.",
  },
  {
    match: /attempts?.exhausted/i,
    message:
      "This campaign has stopped collecting after repeated declines. Save a working card to start it again.",
  },
];

export function fundingErrorMessage(error: unknown, fallback: string): string {
  const raw = apiErrorMessage(error, fallback);
  return FRIENDLY.find((f) => f.match.test(raw))?.message ?? raw;
}
